import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useGamification } from '../components/gamification/GamificationContext';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { doc, getDoc, collection, query, where, orderBy, onSnapshot, setDoc, Timestamp } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import LinkifyText from '../components/common/LinkifyText';

/**
 * Support/FAQ Page - Comprehensive Help Center
 * 
 * Provides detailed how-to guides, FAQs, and step-by-step instructions for all CRM features.
 * Content is organized by feature area and filtered based on user permissions.
 * All FAQ content is stored in Firestore and managed via the Knowledge Management admin page.
 * 
 * Firestore Collections:
 * - supportPageSections: FAQ section definitions (id, title, icon, order, requiredPermission)
 * - supportPageFaqs: Individual FAQ items linked to sections
 * 
 * Last Updated: January 2026
 */
const SupportPage = () => {
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  const { recordActivity } = useGamification();
  const [openSection, setOpenSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [sections, setSections] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supportUrls, setSupportUrls] = useState({
    contact: 'mailto:support@sofibella.com',
    email: 'mailto:help@sofibella.com',
    tutorials: 'https://www.youtube.com/@ApparelCRM'
  });
  const [userFeedback, setUserFeedback] = useState({}); // Track user's feedback on FAQs
  const [submittingFeedback, setSubmittingFeedback] = useState(null); // Track which FAQ feedback is being submitted

  // Load user's existing FAQ feedback
  useEffect(() => {
    if (!currentUser) return;
    
    const feedbackQuery = query(
      collection(db, collectionPath('faqFeedback')),
      where('userId', '==', currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(feedbackQuery, (snapshot) => {
      const feedbackMap = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        feedbackMap[data.faqId] = {
          isHelpful: data.isHelpful,
          timestamp: data.timestamp?.toDate?.() || new Date(data.timestamp)
        };
      });
      setUserFeedback(feedbackMap);
    }, (err) => {
      console.error('Error loading user feedback:', err);
    });
    
    return () => unsubscribe();
  }, [currentUser]);

  // Load sections and FAQs from Firestore
  useEffect(() => {
    // Real-time listener for sections
    const sectionsQuery = query(
      collection(db, collectionPath('supportPageSections')),
      where('isActive', '==', true),
      orderBy('order', 'asc')
    );
    
    const unsubscribeSections = onSnapshot(sectionsQuery, (snapshot) => {
      const sectionData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSections(sectionData);
    }, (err) => {
      console.error('Error loading sections:', err);
    });

    // Real-time listener for FAQs
    const faqsQuery = query(
      collection(db, collectionPath('supportPageFaqs')),
      where('isActive', '==', true),
      orderBy('order', 'asc')
    );
    
    const unsubscribeFaqs = onSnapshot(faqsQuery, (snapshot) => {
      const faqData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFaqs(faqData);
      setLoading(false);
    }, (err) => {
      console.error('Error loading FAQs:', err);
      setLoading(false);
    });

    return () => {
      unsubscribeSections();
      unsubscribeFaqs();
    };
  }, []);

  // Load support URLs from System Settings
  useEffect(() => {
    const loadSupportUrls = async () => {
      try {
        const contactDoc = await getDoc(doc(db, collectionPath('systemSettings'), 'support_contact_url'));
        const emailDoc = await getDoc(doc(db, collectionPath('systemSettings'), 'support_email_url'));
        const tutorialsDoc = await getDoc(doc(db, collectionPath('systemSettings'), 'support_tutorials_url'));
        
        setSupportUrls({
          contact: contactDoc.exists() ? (contactDoc.data().value || contactDoc.data().content) : 'mailto:support@sofibella.com',
          email: emailDoc.exists() ? (emailDoc.data().value || emailDoc.data().content) : 'mailto:help@sofibella.com',
          tutorials: tutorialsDoc.exists() ? (tutorialsDoc.data().value || tutorialsDoc.data().content) : 'https://www.youtube.com/@ApparelCRM'
        });
      } catch (err) {
        console.error('Error loading support URLs from settings:', err);
      }
    };
    
    loadSupportUrls();
  }, []);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const navigateToSection = (sectionId) => {
    setSearchQuery('');
    setOpenSection(sectionId);
    // Scroll after the state update re-renders the section
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const toggleItem = (sectionId, itemIdx) => {
    const key = `${sectionId}-${itemIdx}`;
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Submit FAQ feedback
  const handleFaqFeedback = async (faqId, isHelpful) => {
    if (!currentUser) {
      alert('Please log in to provide feedback');
      return;
    }
    
    setSubmittingFeedback(faqId);
    
    try {
      const feedbackId = `${currentUser.uid}_${faqId}_${Date.now()}`;
      await setDoc(doc(db, collectionPath('faqFeedback'), feedbackId), {
        faqId,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        isHelpful,
        timestamp: Timestamp.now(),
        createdAt: Timestamp.now()
      });
      
      // Gamification: Award points for FAQ feedback (with anti-abuse validation)
      try { 
        await recordActivity('FAQ_FEEDBACK_SUBMITTED', {
          entityId: faqId,
          source: 'manual'
        }); 
      } catch (e) { console.warn('Gamification failed:', e); }
      
      console.log(`FAQ feedback submitted: ${faqId} - ${isHelpful ? 'helpful' : 'not helpful'}`);
    } catch (err) {
      console.error('Error submitting FAQ feedback:', err);
      alert('Error submitting feedback. Please try again.');
    } finally {
      setSubmittingFeedback(null);
    }
  };

  // Build FAQ sections from Firestore data (group FAQs by section)
  const faqSectionsFromFirestore = useMemo(() => {
    return sections.map(section => ({
      ...section,
      items: faqs.filter(faq => faq.sectionId === section.id)
    }));
  }, [sections, faqs]);

  // Filter FAQ sections based on user permissions and search query
  const filteredSections = useMemo(() => {
    return faqSectionsFromFirestore
      .filter(section => {
        if (!section.requiredPermission) return true;
        return can(section.requiredPermission);
      })
      .map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.keywords || []).some(k => k.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      }))
      .filter(section => section.items.length > 0);
  }, [faqSectionsFromFirestore, searchQuery, can]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Help Center</h1>
          <p className="text-gray-600">
            Find step-by-step guides, how-to's, and answers to common questions about ApparelCRM.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for help... (e.g., 'move opportunity stage', 'create campaign')"
              className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 absolute left-4 top-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <button
            onClick={() => navigateToSection('getting-started')}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 text-left"
          >
            <div className="text-2xl mb-2">🚀</div>
            <h3 className="font-semibold text-gray-900">Getting Started</h3>
            <p className="text-sm text-gray-600 mt-1">New to the CRM?</p>
          </button>
          
          <button
            onClick={() => navigateToSection('best-practices')}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 text-left"
          >
            <div className="text-2xl mb-2">💡</div>
            <h3 className="font-semibold text-gray-900">Best Practices</h3>
            <p className="text-sm text-gray-600 mt-1">Work smarter</p>
          </button>
          
          <button
            onClick={() => navigateToSection('troubleshooting')}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 text-left"
          >
            <div className="text-2xl mb-2">🔧</div>
            <h3 className="font-semibold text-gray-900">Troubleshooting</h3>
            <p className="text-sm text-gray-600 mt-1">Fix common issues</p>
          </button>

          <Link
            to="/support/release-notes"
            title="See new features and bug fixes by month"
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 text-left"
          >
            <div className="text-2xl mb-2">📰</div>
            <h3 className="font-semibold text-gray-900">What's New</h3>
            <p className="text-sm text-gray-600 mt-1">Release notes</p>
          </Link>
          
          <a
            href={supportUrls.contact}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 text-left"
          >
            <div className="text-2xl mb-2">💬</div>
            <h3 className="font-semibold text-gray-900">Contact Support</h3>
            <p className="text-sm text-gray-600 mt-1">Get help from us</p>
          </a>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-4">
          {filteredSections.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mx-auto text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600">No results found for "{searchQuery}"</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-indigo-600 hover:text-indigo-700"
              >
                Clear search
              </button>
            </div>
          ) : (
            filteredSections.map((section) => (
              <div key={section.id} id={section.id} className="bg-white rounded-lg shadow">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{section.icon}</span>
                    <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                    <span className="ml-3 text-sm text-gray-500">({section.items.length})</span>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-6 w-6 text-gray-400 transform transition-transform ${
                      openSection === section.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {openSection === section.id && (
                  <div className="px-6 pb-4 border-t border-gray-200">
                    {section.items.map((item, idx) => {
                      const itemKey = `${section.id}-${idx}`;
                      const isExpanded = expandedItems[itemKey];
                      const faqId = item.id; // Firestore FAQ ID
                      const feedback = faqId ? userFeedback[faqId] : null;
                      const isSubmitting = submittingFeedback === faqId;
                      
                      return (
                        <div key={idx} className="py-4 border-b border-gray-100 last:border-0">
                          <button
                            onClick={() => toggleItem(section.id, idx)}
                            className="w-full text-left flex items-start justify-between"
                          >
                            <h3 className="font-semibold text-gray-900 pr-4">{item.question}</h3>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-5 w-5 text-gray-400 flex-shrink-0 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {isExpanded && (
                            <div className="mt-3">
                              <p className="text-gray-600 mb-3"><LinkifyText text={item.answer} /></p>
                              {item.steps && item.steps.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-4 mb-3">
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Step-by-Step:</h4>
                                  <ol className="list-decimal list-inside space-y-1">
                                    {item.steps.map((step, stepIdx) => (
                                      <li key={stepIdx} className="text-sm text-gray-600">{step}</li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                              
                              {/* FAQ Feedback Buttons */}
                              {faqId && currentUser && (
                                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                                  <span className="text-sm text-gray-600">Was this helpful?</span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleFaqFeedback(faqId, true)}
                                      disabled={isSubmitting || feedback?.isHelpful === true}
                                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                                        feedback?.isHelpful === true
                                          ? 'bg-green-100 text-green-700 border-2 border-green-500'
                                          : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-600 border-2 border-transparent'
                                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                                      title={feedback?.isHelpful === true ? 'You found this helpful' : 'Mark as helpful'}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.633z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5V21M3.75 10.5h2.883" />
                                      </svg>
                                      {feedback?.isHelpful === true && <span className="text-sm">Helpful!</span>}
                                    </button>
                                    
                                    <button
                                      onClick={() => handleFaqFeedback(faqId, false)}
                                      disabled={isSubmitting || feedback?.isHelpful === false}
                                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                                        feedback?.isHelpful === false
                                          ? 'bg-red-100 text-red-700 border-2 border-red-500'
                                          : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 border-2 border-transparent'
                                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                                      title={feedback?.isHelpful === false ? 'You found this not helpful' : 'Mark as not helpful'}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5" style={{ transform: 'rotate(180deg)' }}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.633z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5V21M3.75 10.5h2.883" />
                                      </svg>
                                      {feedback?.isHelpful === false && <span className="text-sm">Not helpful</span>}
                                    </button>
                                  </div>
                                  {isSubmitting && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* AI Assistant Notice */}
        <div className="mt-8 p-6 bg-indigo-50 rounded-lg border border-indigo-200">
          <div className="flex items-start">
            <span className="text-2xl mr-3">🤖</span>
            <div>
              <h3 className="text-lg font-semibold text-indigo-900 mb-2">Need Quick Help?</h3>
              <p className="text-indigo-800 mb-3">
                Use the <strong>CRM Assistant</strong> chat in the bottom-left corner for instant answers! 
                Just ask a question like "How do I move an opportunity to a different stage?" and get step-by-step guidance.
              </p>
              <p className="text-sm text-indigo-700">
                The assistant learns from your feedback - give a thumbs up or down to help improve responses.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-4 p-6 bg-white rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Still Need Help?</h3>
          <p className="text-gray-600 mb-4">
            Can't find what you're looking for? Our support team is here to help!
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={supportUrls.contact}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Contact Support
            </a>
            <a
              href={supportUrls.tutorials}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-white text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
            >
              Watch Tutorials
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SupportPage;
